import { useMemo } from "react";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { UseFormReturn } from "react-hook-form";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import CascateList from "../../../../../../common/components/cascate_list";
import dayjs from "dayjs";
import { InspecaoPatrimonioModel } from "../../../../domain/models/inspecao_patrimonio";
import { InspecaoPatrimonioForm } from "../../../schemas/InspecaoPatrimonio_cadastro/inspecao_patrimonio_cadastro";
import { PatrimonioChecklistInfo } from "../../../../domain/models/patrimonio_checklist_info";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";

/**
 * InformacoesGeraisPanel
 */

type Props = {
  statusChecklistMaquinaEquip: number | null;
  lockFuncionarioInspecionado: boolean;
  lockFuncionarioAprovado: boolean;
  patrimonioChecklistInfo: PatrimonioChecklistInfo | null;
  inspecaoPatrimonio: InspecaoPatrimonioModel | null;
  funcionariosDropDown: FuncionarioDropDown[];
  hookForm: UseFormReturn<InspecaoPatrimonioForm>;
};

export const InformacoesGeraisPanel = ({
  statusChecklistMaquinaEquip,
  lockFuncionarioInspecionado,
  lockFuncionarioAprovado,
  patrimonioChecklistInfo,
  inspecaoPatrimonio,
  funcionariosDropDown,
  hookForm,
}: Props) => {
  const [
    idFuncionarioInspecionado,
    idFuncionarioAprovado,
    filtrarFuncionariosPorCentroCusto,
  ] = hookForm.watch([
    'idFuncionarioInspecionado',
    'idFuncionarioAprovado',
    'filtrarFuncionariosPorCentroCusto',

  ]);

  const inspecionadosDropDownFiltrados = useMemo(() => {
    const inspecionados = funcionariosDropDown ?? [];
    return filtrarFuncionariosPorCentroCusto && patrimonioChecklistInfo
      ? inspecionados.filter(x => {
        if (idFuncionarioInspecionado && x.idFuncionario === idFuncionarioInspecionado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return x.idCentroCusto === patrimonioChecklistInfo.idCentroCusto;
      })
      : inspecionados.filter(x => {
        if (idFuncionarioInspecionado && x.idFuncionario === idFuncionarioInspecionado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return true;
      });
  }, [
    patrimonioChecklistInfo,
    funcionariosDropDown,
    filtrarFuncionariosPorCentroCusto,
    idFuncionarioInspecionado,
  ]);

  const aprovadosDropDownFiltrados = useMemo(() => {
    const aprovados = funcionariosDropDown ?? [];
    return filtrarFuncionariosPorCentroCusto &&
      patrimonioChecklistInfo
      ? aprovados.filter(x => {
        if (idFuncionarioAprovado && x.idFuncionario === idFuncionarioAprovado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return x.idCentroCusto === patrimonioChecklistInfo.idCentroCusto;
      })
      : aprovados.filter(x => {
        if (idFuncionarioAprovado && x.idFuncionario === idFuncionarioAprovado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return true;
      });
  }, [
    patrimonioChecklistInfo,
    funcionariosDropDown,
    filtrarFuncionariosPorCentroCusto,
    idFuncionarioAprovado,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Verificação Não Iniciada',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 3,
        label: 'Verificação Iniciada',
        style: {
          backgroundColor: '#89c4f4',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Encerrada',
        style: {
          backgroundColor: '#009c4e',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  // titleBackgroundColor='bg-gray-100 dark:bg-gray-700'
  return (
    <CascateList title="Informações Gerais" fontWeight="bold" defaultOpen>
      <div className="w-full flex flex-col gap-2">
        <div className="text-black dark:text-white">
          <span className="font-bold me-2">Data abertura:</span>
          <span>{inspecaoPatrimonio?.dataInspecao && dayjs(inspecaoPatrimonio.dataInspecao).format('DD/MM/YYYY')}</span>
        </div>

        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div>
            <DefaultFormCheckBox
              name='filtrarFuncionariosPorCentroCusto'
              label='Exibir por centro de custo?'
              readonly={lockFuncionarioAprovado}
              reactHookForm={hookForm}
            />

            <DefaultFormAutocomplete
              name='idFuncionarioInspecionado'
              label='Inspecionado por?'
              readOnly={lockFuncionarioInspecionado || statusChecklistMaquinaEquip === 1}
              options={inspecionadosDropDownFiltrados}
              getOptionDisabled={x => !x.canBeSelected}
              getOptionLabel={x => x.nome}
              getOptionKey={x => x.idFuncionario}
              reactHookForm={hookForm}
            />
          </div>

          <div className="w-full mt-[42px]">
            <DefaultFormAutocomplete
              name='idFuncionarioAprovado'
              label='Aprovado por?'
              readOnly={lockFuncionarioAprovado || statusChecklistMaquinaEquip === 1}
              options={aprovadosDropDownFiltrados}
              getOptionDisabled={x => !x.canBeSelected}
              getOptionLabel={x => x.nome}
              getOptionKey={x => x.idFuncionario}
              reactHookForm={hookForm}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-4 items-center">
          <div className="lg:col-span-2">
            <FormRadioGroupInput
              name='status'
              label='Status'
              readOnly={statusChecklistMaquinaEquip === 1}
              options={statusOptions}
              reactHookForm={hookForm}
            />
          </div>

          <div className="lg:col-span-1">
            <FormDatePickerInput
              name='dataEncerramento'
              label='Data de Encerramento'
              readOnly={lockFuncionarioAprovado || statusChecklistMaquinaEquip === 1}
              reactHookForm={hookForm}
            />
          </div>
        </div>
      </div>
    </CascateList>
  );
}